// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Trash2,
  RefreshCw,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Toast from "../components/Toast";
import { supabase } from "../utils/supabaseClient";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

const Dashboard = ({ darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [quota, setQuota] = useState(null);
  const [toast, setToast] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // ✅ Toast helper
  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ✅ Unified fetch for user quota and papers
  const fetchData = async () => {
    try {
      setRefreshing(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const userId = user?.id;
      if (!userId) {
        showToast("User not authenticated", "error");
        return;
      }

      const [qRes, pRes] = await Promise.all([
        fetch(`${API_URL}/user/quota/${userId}`),
        fetch(`${API_URL}/papers/${userId}`),
      ]);

      if (!qRes.ok || !pRes.ok) {
        console.error("Fetch failed:", qRes.status, pRes.status);
        showToast("Failed to load data from server.", "error");
        return;
      }

      const quotaData = await qRes.json();
      const paperData = await pRes.json();

      setQuota(quotaData);
      setPapers(Array.isArray(paperData) ? paperData : []);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      showToast("Error fetching data.", "error");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Poll backend every 10 seconds for in-progress tasks
  useEffect(() => {
    const interval = setInterval(async () => {
      const inProgress = papers.filter(
        (p) =>
          p.status === "queued" ||
          p.status === "processing" ||
          p.status === "PENDING"
      );
      if (inProgress.length === 0) return;

      try {
        const updates = await Promise.all(
          inProgress.map(async (p) => {
            const taskId = p.tasks?.task_id;
            if (!taskId) return p;
            const res = await fetch(`${API_URL}/task-status/${taskId}`);
            const data = await res.json();
            if (data.status === "SUCCESS") return { ...p, status: "completed" };
            if (data.status === "FAILURE") return { ...p, status: "failed" };
            return p;
          })
        );
        const merged = papers.map(
          (p) => updates.find((u) => u.id === p.id) || p
        );
        setPapers(merged);
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [papers]);

  // ✅ Actions
  const handleView = (paperId) => navigate(`/results/${paperId}`);

  const handleDelete = (paperId) => {
    setPapers((prev) => prev.filter((p) => p.id !== paperId));
    showToast("Paper removed from list", "success");
  };

  // ✅ Status helpers
  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "processing":
      case "queued":
      case "PENDING":
        return <Clock className="w-5 h-5 text-yellow-500 animate-pulse" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) =>
    status ? status.charAt(0).toUpperCase() + status.slice(1) : "";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                My Papers
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage and view your uploaded research papers
              </p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center space-x-2 btn-secondary"
              disabled={refreshing}
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>

          {/* Upload Quota */}
          {quota && (
            <div className="card mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Upload Quota
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Plan: <strong>{quota.plan}</strong> &nbsp; | &nbsp; Used:{" "}
                <strong>
                  {quota.monthly_used}/{quota.monthly_quota}
                </strong>{" "}
                ({quota.remaining_uploads} remaining)
              </p>
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mt-2">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      (quota.monthly_used / quota.monthly_quota) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Papers Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Array.isArray(papers) && papers.length > 0 ? (
                    papers.map((paper) => (
                      <tr
                        key={paper.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                          {paper.title || paper.filename}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(paper.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(paper.status)}
                            <span className="text-sm text-gray-900 dark:text-white">
                              {getStatusText(paper.status)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 flex items-center space-x-2">
                          {paper.status === "completed" && (
                            <button
                              onClick={() => handleView(paper.id)}
                              className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900 rounded-lg"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(paper.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">
                        <div className="text-center py-12">
                          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            No papers uploaded yet
                          </p>
                          <button
                            onClick={() => navigate("/upload")}
                            className="btn-primary"
                          >
                            Upload Your First Paper
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
